import { Box } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import { Button } from "@src/appV2/redesign/components/Button";
import { Text } from "@src/appV2/redesign/components/Text";
import { LoginForm } from "@src/appV2/redesign/Login/LoginForm";
import { berlinRebrandTheme } from "@src/appV2/redesign/theme/theme";
import { useState } from "react";

import { AppHandoffButton } from "./AppHandoffButton";

interface EmailInputViewProps {
  emailInputError?: string;
  /**
   * Custom-scheme URL that opens the native app. Present only on mobile
   * browsers where App Links / Universal Links didn't fire (e.g. Gmail/Outlook
   * embedded webviews). When set, we lead with an "Open in Clipboard App"
   * handoff — the app holds the request-time cached email and auto-completes
   * sign-in — and only reveal manual email entry behind "Continue in browser".
   */
  appSchemeUrl?: string;
  onSubmitEmail: (email: string) => void;
}

/**
 * Full-screen verify-email prompt rendered when the magic-link redeemer
 * can't resolve the requester email from cache. Self-wrapped so
 * AuthRedirectView can short-circuit to this without involving either
 * the legacy or redesigned view's render switch.
 *
 * Two-step on a mobile browser (`appSchemeUrl` set): first offer the native-app
 * handoff and "Continue in browser"; only after the user chooses the browser do
 * we reveal the email form. We never show both CTAs at once, and each path is a
 * single deliberate gesture — redemption must stay gated behind a user gesture
 * because email clients / link scanners pre-fetch URLs and would otherwise burn
 * the one-time token. Desktop (no `appSchemeUrl`) shows the form immediately.
 *
 * Reuses the redesign LoginForm in its `verifyingEmail` mode.
 */
export function EmailInputView({
  emailInputError,
  appSchemeUrl,
  onSubmitEmail,
}: EmailInputViewProps) {
  const [email, setEmail] = useState("");
  const [showEmailForm, setShowEmailForm] = useState(!appSchemeUrl);

  return (
    <ThemeProvider theme={berlinRebrandTheme}>
      <Box
        sx={{
          position: "fixed",
          inset: 0,
          zIndex: 1000,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "background.baseDark",
          paddingX: 5,
          paddingTop: "max(24px, var(--safe-area-inset-top))",
          paddingBottom: "max(24px, var(--safe-area-inset-bottom))",
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: 420,
            paddingX: { xs: 5, sm: 7 },
            paddingY: 7,
            backgroundColor: "background.standout",
            borderRadius: (t) => t.borderRadius?.large,
            boxShadow: (t) => t.shadow?.card,
            display: "flex",
            flexDirection: "column",
            gap: 5,
          }}
        >
          {appSchemeUrl && !showEmailForm ? (
            <>
              <Text variant="body1" sx={{ textAlign: "center", color: "base.base" }}>
                It looks like you&apos;re opening this sign-in link on a different device or browser
                than the one you requested it from. Choose where you&apos;d like to finish signing
                in.
              </Text>
              <AppHandoffButton appSchemeUrl={appSchemeUrl} />
              <Button
                fullWidth
                variant="outlined"
                size="large"
                onClick={() => {
                  setShowEmailForm(true);
                }}
              >
                Continue in browser
              </Button>
            </>
          ) : (
            <LoginForm
              verifyingEmail
              email={email}
              loading={false}
              errorMessage={emailInputError}
              setEmail={setEmail}
              onSubmit={async () => {
                onSubmitEmail(email.trim());
              }}
            />
          )}
        </Box>
      </Box>
    </ThemeProvider>
  );
}
